// @ts-ignore
import { serialize } from "borsh";
import {
  Keypair,
  PublicKey,
  SystemProgram,
} from "@solana/web3.js";
import { Program, Provider } from '@project-serum/anchor';
import { generateKeypairFromSeed, GLOBAL_ACCOUNT } from "./utils";


export const CHAT_MESSAGE_ELEMENTS_COUNT = 20;
export const DUMMY_TX_ID = "0000000000000000000000000000000000000000000";
export const DUMMY_CREATED_ON = "0000000000000000";

// const connection = new Connection('http://localhost:8899', 'confirmed');

export class Message {
  text: string = DUMMY_TX_ID;
  ts: string = DUMMY_CREATED_ON; // max milliseconds in date
  creator: PublicKey = PublicKey.default;
  constructor(
    fields: { text: string; ts: string, creator: PublicKey } | undefined = undefined
  ) {
    if (fields) {
      this.text = fields.text;
      this.ts = fields.ts;
      this.creator = fields.creator;
    }
  }
}

export class MessagesInfo{
  messages: Message[] = [];
  pageKey: PublicKey | undefined;
  pageFull: boolean;
  constructor(
    fields: {messages: Message[], pageKey: PublicKey | undefined, pageFull: boolean}
  ) {
    this.messages = fields.messages;
    this.pageKey = fields.pageKey;
    this.pageFull = fields.pageFull;
  }
}

export class Messages {
  texts: string[] = [];
  tss: string[] = []; // max milliseconds in date
  constructor(
    fields: { texts: string[]; tss: string[]} | undefined = undefined
  ) {
    if (fields) {
      this.texts = fields.texts;
      this.tss = fields.tss;
    }
  }
}



export const MessageSchema = new Map([
  [
    Message,
    {
      kind: "struct",
      fields: [
        ["text", "String"],
        ["ts", "String"],
      ],
    },
  ],
]);


export const MessagesSchema = new Map([
  [
    Messages,
    {
      kind: "struct",
      fields: [
        ["texts", ["String"]],
        ["tss", ["String"]],
      ],
    },
  ],
]);


class MessageService {
  CHAT_MESSAGES_SIZE: number = 0;
  setMessagesDataSize() {
    const sampleMessages: Messages  =
      this.getDefaultMessages();
    this.CHAT_MESSAGES_SIZE = serialize(MessagesSchema, sampleMessages).length;
  }

  constructor() {
    this.setMessagesDataSize();
  }

  
  public async getMessagesKeypair(publicKey: PublicKey, seedKey: PublicKey): Promise<Keypair> {
    //return Keypair.generate();
    //console.log('fetching ', publicKey.toString() );
    return await generateKeypairFromSeed(publicKey, 'MESSAGE', seedKey);
  }

  public async getFollowsKeypair(publicKey: PublicKey, seedKey: PublicKey): Promise<Keypair> {
    return await generateKeypairFromSeed(publicKey, 'FOLLOW', seedKey);
  }



  private getDefaultMessages(): Messages {
    const texts: Array<string> = [];
    const tss: Array<string> = [];
    for (let i = 0; i < CHAT_MESSAGE_ELEMENTS_COUNT; i++) {
      texts.push(DUMMY_TX_ID);
      tss.push(DUMMY_CREATED_ON);
    } 
    const messagesObj = new Messages({texts: texts, tss: tss});
    return messagesObj;
  }

  public async reportGlobalMessages(program: Program): Promise<Message[]> {
    const messages: Array<Message> = [];
    const account = await program.account.globalAccount.fetch(GLOBAL_ACCOUNT);
    for (let i = 0; i < account.dataList.length; i++) {
      messages.push(new Message({text: account.dataList[i], ts: account.timestamps[i].toString(), creator: account.creators[i].toString()}))
    }
    function sortMessages(a: Message, b:Message){
      if (a.ts === b.ts) {
        return 0;
    } else {
        return a.ts > b.ts ? -1 : 1;
    }    }
    return messages.sort(sortMessages);
  }

  public async reportMessagesAndPage(program: Program, publicKey: PublicKey): Promise<MessagesInfo> {
    const messages: Array<Message> = [];
    const pageKeypair = await this.getMessagesKeypair(publicKey, program.programId);
    let pageKey;
    let nextPageKey = pageKeypair.publicKey;
    let pageFull = false;
    while (true) {
      //console.log("next page ", nextPageKey.toString());
      if (nextPageKey.toString() === PublicKey.default.toString()) {
        break;
      }

      const accountInfo = await program.provider.connection.getAccountInfo(nextPageKey);
      if (accountInfo === null) {
        break;
      }
      pageKey = nextPageKey;
      const account = await program.account.baseAccount.fetch(pageKey);
      for (let i = 0; i < account.dataList.length; i++) {
        messages.push(new Message({text: account.dataList[i], ts: account.timestamps[i].toString(), creator: publicKey}))
      }
      pageFull = account.dataList.length === account.maxLength;
      nextPageKey = account.nextPage;
    }
    function sortMessages(a: Message, b:Message){
      if (a.ts === b.ts) {
        return 0;
    } else {
        return a.ts > b.ts ? -1 : 1;
    }    }
    return new MessagesInfo({messages: messages.sort(sortMessages), pageKey: pageKey, pageFull: pageFull});
  }

  public async getFollows(program: Program, publicKey: PublicKey) : Promise<PublicKey[] | undefined> {
    const followAccount = await this.getFollowsKeypair(publicKey, program.programId);
    const accountInfo = await program.provider.connection.getAccountInfo(followAccount.publicKey);
    if (accountInfo === null) {
      console.log('Cannot find the follow account');
      return undefined;
    }
    const account = await program.account.followAccount.fetch(followAccount.publicKey);
    return account.followList
  }
  
  public async initializeFollowAccount(provider: Provider, program: Program, followAccount: Keypair, followKey: PublicKey) : Promise<void> {
    try {
      await program.rpc.initializeFollow(provider.wallet.publicKey, followKey, {
        accounts: {
          followAccount: followAccount.publicKey,
          user: provider.wallet.publicKey,
          systemProgram: SystemProgram.programId,
        },
        signers: [followAccount]
      });
    }
    catch (err) {
      console.log("Transaction error: ", err);
     }
    const account = await program.account.followAccount.fetch(followAccount.publicKey);
    console.log('follow account: ', account);
  }


  public async followAccount(provider: Provider, program: Program, followAccount: PublicKey, followKey: PublicKey) : Promise<void> {
    try {
    await program.rpc.addFollow(followKey, {
      accounts: {
        followAccount: followAccount,
        authority: provider.wallet.publicKey,
      }
    });
  }
  catch (err) {
    console.log("Transaction error: ", err);
   }
    const account = await program.account.followAccount.fetch(followAccount);
    console.log('follow account: ', account);
  }


  public async unfollowAccount(provider: Provider, program: Program, followAccount: PublicKey, followKey: PublicKey) : Promise<void> {
    try {
    await program.rpc.deleteFollow(followKey, {
      accounts: {
        followAccount: followAccount,
        authority: provider.wallet.publicKey,
      }
    });
  }
    catch (err) {
      console.log("Transaction error: ", err);
     }
    const account = await program.account.followAccount.fetch(followAccount);
    console.log('follow account: ', account);
  }


}

const messageService = new MessageService();
export default messageService;
